import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

type SeoProps = {
  title?: string;
  description?: string;
  image?: string;
  canonicalPath?: string;
  noIndex?: boolean;
};

const Seo: React.FC<SeoProps> = ({ title, description, image, canonicalPath, noIndex }) => {
  const location = useLocation();

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const canonicalUrl =
    origin && (canonicalPath || location.pathname)
      ? `${origin}${canonicalPath ?? location.pathname}${location.search}`
      : undefined;

  const resolvedTitle = title ? `${title} | MoodCraft` : 'MoodCraft - AI Mental Health Companion';
  const resolvedDescription = description ?? 'MoodCraft - Mental Health and Wellness App';
  const resolvedImage = origin
    ? new URL(image ?? '/logo512.png', origin).toString()
    : image ?? '/logo512.png';

  const robots = noIndex ? 'noindex,nofollow' : 'index,follow';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'MoodCraft',
    url: origin || undefined,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${origin || ''}/?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={robots} />

      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content="website" />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:site_name" content="MoodCraft" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default Seo;
